/**
 * CCTV 기기 등록/수정 폼 페이지
 */

import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Button, Form, Spinner, Row, Col } from 'react-bootstrap';
import AdminLayout from '../../components/layout/AdminLayout';
import { cctvService } from '../../services/cctvService';
import { CctvDevice } from '../../types/cctv';

interface CctvDeviceFormData {
  device_name: string;
  device_code: string;
  location?: string;
  stream_url?: string;
  access_url?: string;
  status: 'active' | 'inactive' | 'maintenance';
}

const CctvDeviceForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const deviceId = id && id !== 'new' ? parseInt(id, 10) : 0;
  const isEdit = !!deviceId && deviceId > 0;

  const { register, handleSubmit, formState: { errors }, reset } = useForm<CctvDeviceFormData>({
    defaultValues: {
      status: 'active',
    },
  });

  const { data: device, isLoading } = useQuery({
    queryKey: ['cctvDevice', deviceId],
    queryFn: () => cctvService.getDevice(deviceId),
    enabled: isEdit,
  });

  useEffect(() => {
    if (device && isEdit) {
      reset({
        device_name: device.device_name,
        device_code: device.device_code,
        location: device.location || '',
        stream_url: device.stream_url || '',
        access_url: device.access_url || '',
        status: device.status,
      });
    }
  }, [device, isEdit, reset]);

  const createMutation = useMutation({
    mutationFn: (data: Partial<CctvDevice>) => cctvService.createDevice(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cctvDevices'] });
      navigate('/admin/cctv/devices');
    },
    onError: (error: any) => {
      alert(`등록 실패: ${error.message}`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: Partial<CctvDevice>) => cctvService.updateDevice(deviceId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cctvDevices'] });
      queryClient.invalidateQueries({ queryKey: ['cctvDevice', deviceId] });
      navigate(`/admin/cctv/devices/${deviceId}`);
    },
    onError: (error: any) => {
      alert(`수정 실패: ${error.message}`);
    },
  });

  const onSubmit = (data: CctvDeviceFormData) => {
    const formData: Partial<CctvDevice> = {
      ...data,
    };

    if (isEdit) {
      updateMutation.mutate(formData);
    } else {
      createMutation.mutate(formData);
    }
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <Spinner animation="border" variant="primary" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 style={{ color: '#fff' }}>{isEdit ? 'CCTV 기기 수정' : 'CCTV 기기 등록'}</h1>
          <Button variant="secondary" onClick={() => navigate('/admin/cctv/devices')}>
            목록으로
          </Button>
        </div>

        <Card>
          <Card.Body>
            <Form onSubmit={handleSubmit(onSubmit)}>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>기기명 <span style={{ color: '#ff6b6b' }}>*</span></Form.Label>
                    <Form.Control
                      type="text"
                      {...register('device_name', { required: '기기명을 입력해주세요.' })}
                      isInvalid={!!errors.device_name}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.device_name?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>기기 코드 <span style={{ color: '#ff6b6b' }}>*</span></Form.Label>
                    <Form.Control
                      type="text"
                      {...register('device_code', { required: '기기 코드를 입력해주세요.' })}
                      isInvalid={!!errors.device_code}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.device_code?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>설치 위치</Form.Label>
                    <Form.Control
                      type="text"
                      {...register('location')}
                      placeholder="예: 1층 로비, 2층 전시관 입구"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>스트림 URL (RTSP)</Form.Label>
                    <Form.Control
                      type="text"
                      {...register('stream_url')}
                      placeholder="rtsp://..."
                    />
                    <Form.Text className="text-muted">
                      RTSP 스트리밍 URL을 입력하세요.
                    </Form.Text>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>접속 URL (HTTP)</Form.Label>
                    <Form.Control
                      type="text"
                      {...register('access_url')}
                      placeholder="http://..."
                    />
                    <Form.Text className="text-muted">
                      HTTP 스트리밍 URL을 입력하세요.
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>상태 <span style={{ color: '#ff6b6b' }}>*</span></Form.Label>
                    <Form.Select
                      {...register('status', { required: '상태를 선택해주세요.' })}
                      isInvalid={!!errors.status}
                    >
                      <option value="active">활성</option>
                      <option value="inactive">비활성</option>
                      <option value="maintenance">점검중</option>
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.status?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>

              <div className="d-flex justify-content-end gap-2">
                <Button variant="secondary" onClick={() => navigate('/admin/cctv/devices')}>
                  취소
                </Button>
                <Button variant="primary" type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                  {createMutation.isPending || updateMutation.isPending ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      {isEdit ? '수정 중...' : '등록 중...'}
                    </>
                  ) : (
                    isEdit ? '수정' : '등록'
                  )}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default CctvDeviceForm;

